import { Injectable } from 'angular-ts-decorators';

import { DateSource } from '@shared/app/scripts/services/date-source';
import type { LocalStorage } from '@shared/app/scripts/services/local-storage';
import type { Moment } from '@shared/app/scripts/vendor/moment';
import { WindowStreams } from '@core/app/scripts/services/window-streams';

import { Observable } from 'rxjs';
import {
  filter,
  map,
  mapTo,
  shareReplay,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';

/**
 * A service that takes care of inter tab communication.
 */
@Injectable('interTab')
export class InterTab {
  constructor(
    private localStorage: LocalStorage,
    private dateSource: DateSource,
    private moment: Moment,
    private windowStreams: WindowStreams,
  ) {
    'ngInject';
  }

  private tabLock$: Observable<boolean>;
  private KEY = 'tabLock';

  /**
   * Start a lock, that will allow for blocking the app, if it's being used by
   * any other tab.
   * @return {Observable<boolean>}
   * emits true when started, and false as soon as it's deemed to be locked by
   * other app instance.
   */
  getLock(): Observable<boolean> {
    // doesn't start more locks then necessary
    if (this.tabLock$) {
      return this.tabLock$;
    }

    const lockDate = this.dateSource.now(),
      lockValue = lockDate.toJSON(),
      lastLock = this.localStorage[this.KEY];

    this.localStorage[this.KEY] = lockValue;

    const lockChanges$ = this.windowStreams.storage$.pipe(
        filter((value: any) => value?.storageArea?.tabLock),
        map((value: any) => value.storageArea.tabLock),
        filter((incommingLock) => incommingLock !== lockValue),
        shareReplay(1),
      ),
      kickedOut$ = lockChanges$.pipe(
        filter((incommingLock) => {
          if (incommingLock === lastLock) {
            return true;
          }

          let incommingMoment = this.moment(new Date(incommingLock));

          if (incommingMoment.isBefore(lockDate)) {
            return true;
          }

          return false;
        }),
        take(1),
        shareReplay(),
      );

    // force own value over anything else besides the lastLock
    lockChanges$.pipe(takeUntil(kickedOut$)).subscribe(() => {
      this.localStorage[this.KEY] = lockValue;
    });

    this.tabLock$ = kickedOut$.pipe(
      mapTo(false),
      startWith(true),
      shareReplay(1),
    );

    return this.tabLock$;
  }
}
